import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../utils/api';
import { formatPrice } from '../utils/translations';

const Analytics = ({ adminLang }) => {
  const [period, setPeriod] = useState('today');
  const [revenueData, setRevenueData] = useState(null);
  const [topDishes, setTopDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const t = {
    en: {
      title: 'Revenue & Analytics',
      period: 'Period',
      today: 'Today',
      week: 'This Week',
      month: 'This Month',
      custom: 'Custom Range',
      startDate: 'Start Date',
      endDate: 'End Date',
      apply: 'Apply',
      totalRevenue: 'Total Revenue',
      totalOrders: 'Total Orders',
      avgOrderValue: 'Average Order Value',
      revenueByCategory: 'Revenue by Category',
      comparisonPrevious: 'vs Previous Period',
      topSellingDishes: 'Top 10 Selling Dishes',
      dishName: 'Dish Name',
      timesOrdered: 'Times Ordered',
      quantity: 'Quantity',
      revenue: 'Revenue',
      resetOrderNumbers: 'Reset Order Numbers',
      resetConfirm: 'Reset order numbers to start fresh? All existing orders will be archived but kept in database.',
      resetSuccess: 'Order numbers reset successfully!',
      noData: 'No data available for this period'
    },
    vi: {
      title: 'Doanh Thu & Phân Tích',
      period: 'Thời Kỳ',
      today: 'Hôm Nay',
      week: 'Tuần Này',
      month: 'Tháng Này',
      custom: 'Tùy Chỉnh',
      startDate: 'Ngày Bắt Đầu',
      endDate: 'Ngày Kết Thúc',
      apply: 'Áp Dụng',
      totalRevenue: 'Tổng Doanh Thu',
      totalOrders: 'Tổng Đơn Hàng',
      avgOrderValue: 'Giá Trị Đơn Hàng Trung Bình',
      revenueByCategory: 'Doanh Thu Theo Danh Mục',
      comparisonPrevious: 'so với Kỳ Trước',
      topSellingDishes: 'Top 10 Món Bán Chạy',
      dishName: 'Tên Món',
      timesOrdered: 'Số Lần Đặt',
      quantity: 'Số Lượng',
      revenue: 'Doanh Thu',
      resetOrderNumbers: 'Đặt Lại Số Đơn Hàng',
      resetConfirm: 'Đặt lại số đơn hàng để bắt đầu mới? Tất cả đơn hàng hiện tại sẽ được lưu trữ nhưng vẫn giữ trong cơ sở dữ liệu.',
      resetSuccess: 'Đặt lại số đơn hàng thành công!',
      noData: 'Không có dữ liệu cho kỳ này'
    }
  };

  const translations = t[adminLang] || t.en;

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const startDate = period === 'custom' ? customStartDate : null;
      const endDate = period === 'custom' ? customEndDate : null;

      const [revenueRes, dishesRes] = await Promise.all([
        analyticsAPI.getRevenue(period, startDate, endDate),
        analyticsAPI.getTopDishes(period, startDate, endDate, 10)
      ]);

      setRevenueData(revenueRes.data);
      setTopDishes(dishesRes.data.top_dishes || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setPeriod('custom');
      loadAnalytics();
    }
  };

  const handleResetOrderNumbers = async () => {
    if (window.confirm(translations.resetConfirm)) {
      try {
        await analyticsAPI.resetOrderNumbers();
        alert(translations.resetSuccess);
      } catch (error) {
        console.error('Error resetting order numbers:', error);
        alert('Error resetting order numbers');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-600 font-bold">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-black text-slate-900">{translations.title}</h2>
        <button
          onClick={handleResetOrderNumbers}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-sm transition-all"
        >
          {translations.resetOrderNumbers}
        </button>
      </div>

      {/* Period Selector */}
      <div className="bg-white p-6 rounded-2xl shadow-sm mb-6">
        <h3 className="font-bold text-lg mb-4">{translations.period}</h3>
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={() => setPeriod('today')}
            className={`px-4 py-2 rounded-lg font-bold transition-all ${
              period === 'today'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {translations.today}
          </button>
          <button
            onClick={() => setPeriod('week')}
            className={`px-4 py-2 rounded-lg font-bold transition-all ${
              period === 'week'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {translations.week}
          </button>
          <button
            onClick={() => setPeriod('month')}
            className={`px-4 py-2 rounded-lg font-bold transition-all ${
              period === 'month'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {translations.month}
          </button>
          <button
            onClick={() => setPeriod('custom')}
            className={`px-4 py-2 rounded-lg font-bold transition-all ${
              period === 'custom'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {translations.custom}
          </button>
        </div>

        {period === 'custom' && (
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-bold mb-2">{translations.startDate}</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-bold mb-2">{translations.endDate}</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <button
              onClick={handleCustomDateApply}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg font-bold transition-all"
            >
              {translations.apply}
            </button>
          </div>
        )}
      </div>

      {revenueData ? (
        <>
          {/* Revenue Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl shadow-lg text-white">
              <p className="text-sm opacity-90 mb-2">{translations.totalRevenue}</p>
              <p className="text-3xl font-black">{formatPrice(revenueData.total_revenue / 1000)}</p>
              <p className="text-sm mt-2 opacity-80">
                {revenueData.revenue_change_percent >= 0 ? '+' : '-'}{' '}
                {Math.abs(revenueData.revenue_change_percent).toFixed(1)}% {translations.comparisonPrevious}
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl shadow-lg text-white">
              <p className="text-sm opacity-90 mb-2">{translations.totalOrders}</p>
              <p className="text-3xl font-black">{revenueData.total_orders}</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-2xl shadow-lg text-white">
              <p className="text-sm opacity-90 mb-2">{translations.avgOrderValue}</p>
              <p className="text-3xl font-black">{formatPrice(revenueData.average_order_value / 1000)}</p>
            </div>
          </div>

          {/* Revenue by Category */}
          <div className="bg-white p-6 rounded-2xl shadow-sm mb-6">
            <h3 className="font-bold text-xl mb-4">{translations.revenueByCategory}</h3>
            <div className="space-y-3">
              {Object.entries(revenueData.revenue_by_category || {}).map(([category, revenue]) => (
                <div key={category} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                  <span className="font-bold text-slate-700">{category}</span>
                  <span className="font-black text-slate-900">{formatPrice(revenue / 1000)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Selling Dishes */}
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <h3 className="font-bold text-xl mb-4">{translations.topSellingDishes}</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left py-3 px-4 font-bold text-slate-700">#{}</th>
                    <th className="text-left py-3 px-4 font-bold text-slate-700">{translations.dishName}</th>
                    <th className="text-center py-3 px-4 font-bold text-slate-700">{translations.timesOrdered}</th>
                    <th className="text-center py-3 px-4 font-bold text-slate-700">{translations.quantity}</th>
                    <th className="text-right py-3 px-4 font-bold text-slate-700">{translations.revenue}</th>
                  </tr>
                </thead>
                <tbody>
                  {topDishes.map((dish, index) => (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-bold text-slate-500">{index + 1}</td>
                      <td className="py-3 px-4 font-bold text-slate-900">{dish.dish_name}</td>
                      <td className="py-3 px-4 text-center">{dish.total_orders}</td>
                      <td className="py-3 px-4 text-center">{dish.total_quantity}</td>
                      <td className="py-3 px-4 text-right font-bold">{formatPrice(dish.total_revenue / 1000)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white p-12 rounded-2xl shadow-sm text-center">
          <p className="text-slate-500 text-lg">{translations.noData}</p>
        </div>
      )}
    </div>
  );
};

export default Analytics;
